// table.ts - Types for table data in AI responses

/**
 * Explicit formatting of a table column. Without it, `formatCellValue` guesses
 * from the field name — fine for the built-in tables, but a customer-configured
 * column ("surface_m2", "headcount") matches no keyword and would render raw.
 */
export type TableColumnType =
  | "text"
  | "number"
  | "money"
  | "area"
  | "percentage"
  | "date";

/**
 * Represents the structure of a table in AI responses
 */
export interface TableData {
  labels: Record<string, string>; // Mapping field_name -> Display Label
  data: Record<string, any>[]; // Array of row objects
  /** Optional warning shown with the table (e.g. selected spaces excluded
   *  from the analysis or alternate polygons counted once). */
  warning?: string;
  /** Per-column formatting; takes precedence over the name-based heuristic. */
  columnTypes?: Record<string, TableColumnType>;
  /**
   * Currency symbol or ISO code for money columns ("$", "€", "EUR"). Defaults
   * to USD, which is what the formatter hard-coded before — it printed "$" on
   * datasets whose source values were in euros.
   */
  currency?: string;
  /** Unit appended to area columns. Defaults to "SF". */
  areaUnit?: string;
}

/** Explicit formatting context for one cell. */
export interface CellFormatHint {
  type?: TableColumnType;
  currency?: string;
  areaUnit?: string;
}

/** Symbols we can map to an ISO code for Intl currency formatting. */
const CURRENCY_CODES: Record<string, string> = {
  $: "USD",
  "€": "EUR",
  "£": "GBP",
  "¥": "JPY",
};

/**
 * Format a money value in the table's currency. Accepts a symbol ("€") or an
 * ISO code ("EUR"); anything else is prefixed as-is rather than silently
 * relabelled as dollars.
 */
export function formatMoney(value: number, currency?: string): string {
  const raw = (currency || "$").trim();
  const code = CURRENCY_CODES[raw] || (/^[A-Za-z]{3}$/.test(raw) ? raw.toUpperCase() : "");
  if (code) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }
  return `${raw}${value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Query interpretation metadata
 */
export interface QueryInterpretation {
  reference_space?: string | null;
  mall_name?: string | null;
  space_header?: string | null;
  tenant_name?: string | null;
  plan_name?: string | null;
}

/**
 * Message result that includes table data
 */
export interface MessageResultWithTables {
  query_interpretation?: QueryInterpretation;
  message: string;
  tables?: TableData;
}

/**
 * Chart.js compatible visualization types
 */
export type ChartType = "bar" | "line" | "doughnut" | "pie" | "radar";

/**
 * Chart.js dataset structure
 */
export interface ChartDataset {
  label?: string;
  data: number[];
  backgroundColor?: string | string[];
  borderColor?: string | string[];
  borderWidth?: number;
  tension?: number;
  fill?: boolean;
}

/**
 * Chart.js data structure
 */
export interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}

/**
 * Chart.js options structure (simplified)
 */
export interface ChartOptions {
  responsive?: boolean;
  maintainAspectRatio?: boolean;
  plugins?: {
    legend?: {
      position?: "top" | "bottom" | "left" | "right";
      display?: boolean;
    };
    title?: {
      display?: boolean;
      text?: string;
    };
  };
  scales?: any;
}

/**
 * Visualization structure compatible with Chart.js
 */
export interface Visualization {
  title: string;
  type: ChartType;
  data: ChartData;
  options?: ChartOptions;
}

/**
 * A single point in a time series stored on a table row.
 * Backends ship a variety of shapes — we support the common ones.
 */
export interface TimeSeriesPoint {
  quarter_end_date?: string;
  date?: string;
  month?: string | number;
  year?: string | number;
  quarter?: number;
  quarterly_sales?: number;
  monthly_sales?: number;
  value?: number;
  [key: string]: any;
}

/**
 * Detect time-series columns by naming convention.
 * Matches `time_series` and any field ending with `_series`.
 */
export function isTimeSeriesField(fieldName: string): boolean {
  if (!fieldName) return false;
  const normalized = fieldName.toLowerCase().replace(/[\s]/g, "_");
  return normalized === "time_series" || normalized.endsWith("_series");
}

/**
 * Pick the best label for a time-series point (quarter end date, generic date,
 * year-quarter pair, or month label).
 */
export function getTimeSeriesPointLabel(point: TimeSeriesPoint): string {
  if (point.quarter_end_date) return String(point.quarter_end_date);
  if (point.date) return String(point.date);
  if (point.year && point.quarter) return `${point.year} Q${point.quarter}`;
  if (point.quarter) return `Q${point.quarter}`;
  if (point.month) return String(point.month);
  if (point.year) return String(point.year);
  return "";
}

/**
 * Pick the best numeric value for a time-series point.
 */
export function getTimeSeriesPointValue(point: TimeSeriesPoint): number {
  if (typeof point.quarterly_sales === "number") return point.quarterly_sales;
  if (typeof point.monthly_sales === "number") return point.monthly_sales;
  if (typeof point.value === "number") return point.value;
  // Fallback: first numeric property that is not quarter/year
  for (const key of Object.keys(point)) {
    if (key === "quarter" || key === "year") continue;
    const v = (point as any)[key];
    if (typeof v === "number") return v;
  }
  return 0;
}

/**
 * Extract aligned labels and values arrays from a time-series column.
 */
export function extractTimeSeriesValues(
  points: TimeSeriesPoint[]
): { labels: string[]; values: number[] } {
  if (!Array.isArray(points)) return { labels: [], values: [] };
  const labels: string[] = [];
  const values: number[] = [];
  for (const p of points) {
    if (!p || typeof p !== "object") continue;
    labels.push(getTimeSeriesPointLabel(p));
    values.push(getTimeSeriesPointValue(p));
  }
  return { labels, values };
}

/**
 * Serialize a time-series array into a compact string for CSV / TSV exports
 * (e.g. "2022-03-31=425068.54; 2022-06-30=506033.97").
 */
export function serializeTimeSeriesForExport(points: TimeSeriesPoint[]): string {
  if (!Array.isArray(points)) return "";
  return points
    .map((p) => {
      const label = getTimeSeriesPointLabel(p);
      const value = getTimeSeriesPointValue(p);
      return label ? `${label}=${value}` : String(value);
    })
    .join("; ");
}

/**
 * Helper function to check if a field name represents a space number
 * Supports exact matches and patterns like "apple_space_numbers", "tenant_space_number", etc.
 */
export function isSpaceNumberField(fieldName: string): boolean {
  const normalizedFieldName = fieldName.toLowerCase().replace(/[\s]/g, "_");

  // Exact match list
  const spaceFields = [
    "space_number",
    "space_numbers",
    "spacenumber",
    "spacenumbers",
    "spaceid",
    "space_id",
  ];

  // Check for exact match
  if (spaceFields.includes(normalizedFieldName)) {
    return true;
  }

  // Check for patterns ending with _space_number(s)
  if (normalizedFieldName.endsWith("_space_number") ||
    normalizedFieldName.endsWith("_space_numbers") ||
    normalizedFieldName.endsWith("_spacenumber") ||
    normalizedFieldName.endsWith("_spacenumbers")) {
    return true;
  }

  return false;
}

/**
 * Format a cell value.
 *
 * With a `hint` (from `TableData.columnTypes` / `currency` / `areaUnit`) the
 * column's declared type decides the formatting. Without one, the legacy
 * name-based heuristic below applies — it recognises the built-in tables' keys
 * but nothing a customer might configure, and it always printed dollars and
 * square feet.
 */
export function formatCellValue(
  fieldName: string,
  value: any,
  hint?: CellFormatHint
): string {
  if (value === null || value === undefined) {
    return "N/A";
  }

  if (hint?.type) {
    return formatByType(value, hint);
  }

  const lowerFieldName = fieldName.toLowerCase();

  // Date fields
  if (lowerFieldName.includes("days")) {
    return value;
  }

  if (
    lowerFieldName.includes("date") ||
    lowerFieldName.includes("expiration")
  ) {
    if (typeof value === "string") {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        }
      } catch (e) {
        // If date parsing fails, return as-is
      }
    }
  }
  // Occupancy Cost is a percentage, not a currency
  if ((lowerFieldName.includes("occupancy") || lowerFieldName.includes("occ")) && lowerFieldName.includes("cost") && !(lowerFieldName.includes("per_sf") || lowerFieldName.includes("psf"))) {
    if (typeof value === "number") {
      return `${value.toFixed(2)}%`;
    }
  }

  // Currency fields (USD)
  if (
    lowerFieldName.includes("usd") ||
    lowerFieldName.includes("rent") ||
    lowerFieldName.includes("sales") ||
    lowerFieldName.includes("cost")
  ) {
    if (typeof value === "number") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    }
  }

  // Square feet fields
  if (lowerFieldName.includes("_sf") || lowerFieldName.includes("area")) {
    if (typeof value === "number") {
      return `${value.toLocaleString()} SF`;
    }
  }

  // Price per square foot
  if (lowerFieldName.includes("_psf") || lowerFieldName.includes("per_sf")) {
    if (typeof value === "number") {
      return `$${value.toFixed(2)}/SF`;
    }
  }

  // Percentage fields
  if (lowerFieldName.includes("percentage") || lowerFieldName.includes("percent")) {
    if (typeof value === "number") {
      return `${value.toFixed(2)}%`;
    }
  }

  // Boolean fields
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  // Number fields
  if (typeof value === "number") {
    return value.toLocaleString();
  }

  // Default: return as string
  return String(value);
}

/** Format a value from its declared column type. */
function formatByType(value: any, hint: CellFormatHint): string {
  const numeric = typeof value === "number";

  switch (hint.type) {
    case "money":
      return numeric ? formatMoney(value, hint.currency) : String(value);
    case "area":
      return numeric
        ? `${value.toLocaleString()} ${hint.areaUnit || "SF"}`
        : String(value);
    case "percentage":
      return numeric ? `${value.toFixed(2)}%` : String(value);
    case "number":
      return numeric ? value.toLocaleString() : String(value);
    case "date": {
      if (typeof value !== "string") return String(value);
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    default:
      return typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
  }
}

/**
 * Helper function to get color class for values containing "Under" or "Over"
 * Returns a Tailwind CSS class string or empty string if no match
 */
export function getUnderOverColorClass(value: any): string {
  if (value === null || value === undefined) return "";
  const strValue = String(value).toLowerCase();
  if (strValue.startsWith("over")) return "text-green-600 font-semibold";
  if (strValue.startsWith("under")) return "text-red-600 font-semibold";
  return "";
}

/**
 * Helper function to export table data as CSV
 */
export function exportTableAsCSV(tableData: TableData, mallName?: string): string {
  const { labels, data } = tableData;
  const fieldNames = Object.keys(labels);

  // Add Mall Name as first column if provided
  const allFieldNames = mallName ? ["mall_name", ...fieldNames] : fieldNames;
  const allLabels = mallName ? { mall_name: "Mall Name", ...labels } : labels;

  // Create header row with labels
  const headerRow = allFieldNames.map((field) => `"${allLabels[field]}"`).join(",");

  // Create data rows
  const dataRows = data.map((row) => {
    const rowWithMall = mallName ? { mall_name: mallName, ...row } : row;
    return allFieldNames
      .map((field) => {
        const value = rowWithMall[field];
        if (value === null || value === undefined) {
          return '""';
        }
        if (isTimeSeriesField(field) && Array.isArray(value)) {
          const serialized = serializeTimeSeriesForExport(value).replace(/"/g, '""');
          return `"${serialized}"`;
        }
        // Escape quotes in values
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      })
      .join(",");
  });

  return [headerRow, ...dataRows].join("\n");
}

/**
 * Helper function to copy table data for pasting into Excel
 * Uses tab-separated values (TSV) format which Excel recognizes
 */
export function copyTableForExcel(tableData: TableData, mallName?: string): string {
  const { labels, data } = tableData;
  const fieldNames = Object.keys(labels);

  // Add Mall Name as first column if provided
  const allFieldNames = mallName ? ["mall_name", ...fieldNames] : fieldNames;
  const allLabels = mallName ? { mall_name: "Mall Name", ...labels } : labels;

  // Create header row with labels (tab-separated)
  const headerRow = allFieldNames.map((field) => allLabels[field]).join("\t");

  // Create data rows (tab-separated)
  const dataRows = data.map((row) => {
    const rowWithMall = mallName ? { mall_name: mallName, ...row } : row;
    return allFieldNames
      .map((field) => {
        const value = rowWithMall[field];
        if (value === null || value === undefined) {
          return "";
        }
        if (isTimeSeriesField(field) && Array.isArray(value)) {
          return serializeTimeSeriesForExport(value);
        }
        return String(value);
      })
      .join("\t");
  });

  return [headerRow, ...dataRows].join("\n");
}

/**
 * Helper function to export table data as Excel (.xlsx) file
 * Returns a Blob that can be downloaded as a file
 */
export async function exportTableAsXLSX(
  tableData: TableData,
  filename: string = "export.xlsx",
  mallName?: string
): Promise<void> {
  // Dynamic import of exceljs to reduce bundle size
  const ExcelJS = await import("exceljs");

  const { labels, data } = tableData;
  const fieldNames = Object.keys(labels);

  // Add Mall Name as first column if provided
  const allFieldNames = mallName ? ["mall_name", ...fieldNames] : fieldNames;
  const allLabels = mallName ? { mall_name: "Mall Name", ...labels } : labels;

  // Create workbook and worksheet
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Data");

  // Add header row using display labels
  worksheet.addRow(allFieldNames.map((field) => allLabels[field]));

  // Add data rows
  data.forEach((row) => {
    const rowWithMall = mallName ? { mall_name: mallName, ...row } : row;
    worksheet.addRow(
      allFieldNames.map((field) => {
        const v = rowWithMall[field];
        if (isTimeSeriesField(field) && Array.isArray(v)) {
          return serializeTimeSeriesForExport(v);
        }
        return v ?? "";
      })
    );
  });

  // Auto-size columns based on content
  worksheet.columns = allFieldNames.map((field, index) => {
    const headerLength = allLabels[field].length;
    const maxDataLength = Math.max(
      ...data.map((row) => {
        const rowWithMall = mallName ? { mall_name: mallName, ...row } : row;
        const value = rowWithMall[field];
        if (isTimeSeriesField(field) && Array.isArray(value)) {
          return serializeTimeSeriesForExport(value).length;
        }
        return value ? String(value).length : 0;
      })
    );
    return {
      key: field,
      width: Math.min(Math.max(headerLength, maxDataLength) + 2, 50)
    };
  });

  // Generate Excel file buffer
  const buffer = await workbook.xlsx.writeBuffer();

  // Create blob and trigger download
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

